import { AskRequest, AskRequestStatus, AskRequestStatuses } from '@hone-automation/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast, showStackableToast, TOAST_ERROR, TOAST_SUCCESS, FIVE_SECONDS, TEN_SECONDS } from 'lib/utils';
import { makeRemotePatchQueueRequestPriority } from 'main/factories/usecases';
import { invalidateQueueRequestsForLocation } from './queueQueryKeys';

type PatchQueueRequestData = {
  txId?: string;
  id?: string;
  priority?: 'Blocker' | 'Normal';
  status?: AskRequestStatus;
  comment?: string;
  assignee?: string | null;
  dueDate?: string | null;
  locationId?: string;
  parentId?: string | null;
  title?: string;
};

/**
 * Get toast key and action text based on the mutation variables
 * Used for stackable toasts that group similar operations
 */
const getToastKeyAndAction = (variables: {
  status?: AskRequestStatus;
  priority?: 'Blocker' | 'Normal';
  assignee?: string | null;
  dueDate?: string | null;
  parentId?: string | null;
}): { key: string; action: string } => {
  // Parent group changes
  if (variables.parentId !== undefined) {
    return variables.parentId
      ? { key: 'bulk-add-to-group', action: 'added to group' }
      : { key: 'bulk-remove-from-group', action: 'removed from group' };
  }
  // Status changes (drag between filter panels)
  if (variables.status === AskRequestStatuses.Closed) {
    return { key: 'bulk-resolve', action: 'resolved' };
  }
  if (variables.status === AskRequestStatuses.Open) {
    return { key: 'bulk-reopen', action: 'reopened' };
  }
  if (variables.status === AskRequestStatuses.In_Progress) {
    return { key: 'bulk-in-progress', action: 'moved to in progress' };
  }
  // Priority changes (block/unblock)
  if (variables.priority === 'Blocker') {
    return { key: 'bulk-block', action: 'marked as blocker' };
  }
  if (variables.priority === 'Normal') {
    return { key: 'bulk-unblock', action: 'unblocked' };
  }
  // Assignee changes
  if (variables.assignee !== undefined) {
    return variables.assignee
      ? { key: 'bulk-assign', action: 'assigned' }
      : { key: 'bulk-unassign', action: 'unassigned' };
  }
  // Due date changes
  if (variables.dueDate !== undefined) {
    return variables.dueDate
      ? { key: 'bulk-due-date', action: 'due date set' }
      : { key: 'bulk-due-date-remove', action: 'due date removed' };
  }
  return { key: 'bulk-update', action: 'updated' };
};

const patchRequestPriority = async (data: PatchQueueRequestData) => {
  const requestId = data.txId || data.id;
  if (!requestId) {
    throw new Error('Request ID is required');
  }
  const service = makeRemotePatchQueueRequestPriority(requestId);
  const result = await service.patch({
    priority: data.priority,
    status: data.status,
    comment: data.comment,
    assignee: data.assignee,
    dueDate: data.dueDate,
    parentId: data.parentId,
    title: data.title,
  });
  return result;
};

/**
 * Hook for patching queue request properties (priority, status, assignee, dueDate)
 * Automatically invalidates queue queries after successful mutation.
 *
 * @param onSuccessCallback - Optional callback called with the updated request data
 */
export const usePatchQueueRequestMutation = (onSuccessCallback?: (trx: AskRequest | undefined) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchRequestPriority,
    onSuccess: (data, variables) => {
      const locationId = variables.locationId;

      // Invalidate queue queries - items may move between views after update
      if (locationId) {
        // Invalidate count query for the specific location
        queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
        queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary', locationId] });
        // Invalidate new query key format (used by useGetQueueRequestsQuery in QueueContext)
        invalidateQueueRequestsForLocation(queryClient, locationId);
        queryClient.invalidateQueries({ queryKey: ['flaggedTransactions', locationId] });
      } else {
        // Invalidate all queue-related queries
        queryClient.invalidateQueries({ queryKey: ['queueRequestsCount'] });
        queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary'] });
        queryClient.invalidateQueries({ predicate: query => query.queryKey[0] === 'queueRequests' });
        queryClient.invalidateQueries({ queryKey: ['flaggedTransactions'] });
      }

      // Use stackable toasts for bulk operations
      const { key, action } = getToastKeyAndAction(variables);
      showStackableToast(key, action, 'success', FIVE_SECONDS);

      if (onSuccessCallback) onSuccessCallback(data as AskRequest);
    },
    onError: (error: any) => {
      showToast(error.message || 'Error while updating request', TOAST_ERROR, TEN_SECONDS);
    },
  });
};
